"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { FiShoppingBag, FiTag } from "react-icons/fi";
import { HiOutlineChevronRight } from "react-icons/hi";
import {
  IoIosCheckmarkCircle,
  IoIosHeart,
  IoMdAdd,
  IoMdHeartEmpty,
  IoMdRemove,
} from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BreadCrumb from "../components/BreadCrumb";
import { postCart } from "../redux/features/cartSlice";
import { getSingleProduct } from "../redux/features/ProductSlice";
import { deleteWishlist, postWishlist } from "../redux/features/WishlistSlice";
import Recommendation from "../sections/detail/Recommendation";

const ProductDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [selectedImage, setSelectedImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedVariation, setSelectedVariation] = useState({});
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const wishLists = useSelector((state) => state.wishlist.wishlist);
  const isLoading = useSelector((state) => state.wishlist.isLoading);
  const me = useSelector((state) => state.login.me);

  const singleProduct = useSelector(
    (state) => state.mainproduct?.singleProduct
  );
  const cart = useSelector((state) => state.cart.carts);

  useEffect(() => {
    dispatch(getSingleProduct({ slug: slug }));
  }, [dispatch, slug]);

  useEffect(() => {
    if (singleProduct) {
      setSelectedVariation(singleProduct?.variations?.[0]);
      setSelectedImage(singleProduct?.gallery?.[0]?.url);
    }
  }, [singleProduct]);

  const isDuplicate = cart?.some((cartItem) => {
    if (singleProduct?.variations && singleProduct.variations.length > 0) {
      // For products with variations, check variation.id
      return (
        cartItem?.variation?.product?.id === singleProduct?.id &&
        cartItem?.variation?.id === selectedVariation?.id
      );
    } else {
      // For products without variations, check product.id
      return cartItem?.product?.id === singleProduct?.id;
    }
  });

  const alreadyWished = wishLists.some(
    (item) => item?.product_id === singleProduct?.id
  );

  const toggleHeart = () => {
    toast.dismiss();
    if (!me) {
      navigate("/register");
    }
    if (alreadyWished) {
      dispatch(
        deleteWishlist({
          id: wishLists?.find((data) => data?.product?.id === singleProduct?.id)
            ?.id,
        })
      );
    } else {
      dispatch(
        postWishlist({ user_id: me?.id, product_id: singleProduct?.id })
      );
    }
  };

  const incrementQuantity = () => {
    // Check if the quantity is less than or equal to the selected variation's stock
    const stock =
      singleProduct?.variations && singleProduct.variations.length > 0
        ? selectedVariationStock
        : singleProduct?.stock;

    if (quantity >= stock) {
      toast.error("Quantity limit exceeded");
      return;
    }

    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  const handleVariationClick = (type, value) => {
    // Update the selected variation for the given type
    const updatedVariation = {
      ...selectedVariation,
      [type]: value,
    };

    // Find the selected variation based on the updated options
    const selected = singleProduct?.variations?.find((v) => {
      const fieldsToCompare = ["size", "color", "brand", "litre"].filter(
        (field) => updatedVariation?.[field] !== undefined
      );

      return fieldsToCompare.every(
        (field) => v[field] === updatedVariation[field]
      );
    });

    // If the selected variation is out of stock, show an error
    // if (selected && selected.stock <= 0) {
    //   toast.error("This variation is out of stock");
    //   return;
    // }

    // Update the selected variation state
    setSelectedVariation(selected);

    // Reset quantity to 1 if the selected variation changes
    setQuantity(1);
  };

  const handleAddToCart = () => {
    // If there are no variations, add the product directly to the cart
    if (!singleProduct?.variations || singleProduct.variations.length === 0) {
      const cartItem = {
        product: singleProduct.id, // Use product ID instead of variation ID
        quantity: quantity,
      };

      // Check for duplicates
      const isProductDuplicate = cart?.some(
        (cartItem) => cartItem.product === singleProduct.id
      );

      if (isProductDuplicate) {
        toast.error("Item already in cart");
      } else {
        dispatch(postCart(cartItem))
          .unwrap()
          .then(() => {
            toast.success("Successfully added to cart");
          })
          .catch((error) => {
            navigate("/register");
          });
      }
      return;
    }

    // If variations exist, proceed with variation selection logic
    // Check if all required variations are selected
    const requiredVariations = ["size", "color", "brand", "litre"].filter(
      (type) => singleProduct?.variations?.some((v) => v[type])
    );

    const isAllVariationsSelected = requiredVariations.every(
      (type) => selectedVariation?.[type]
    );

    if (!isAllVariationsSelected) {
      toast.error("Please select all required variations");
      return;
    }

    // Find the selected variation based on the selected options
    const selected = singleProduct?.variations?.find((v) => {
      const fieldsToCompare = ["size", "color", "brand", "litre"].filter(
        (field) => selectedVariation?.[field] !== undefined
      );

      return fieldsToCompare.every(
        (field) => v[field] === selectedVariation[field]
      );
    });

    if (!selected) {
      toast.error("Selected variation not found");
      return;
    }

    // // Check if the selected variation is out of stock
    // if (selected.stock <= 0) {
    //   toast.error("This variation is out of stock");
    //   return;
    // }

    const cartItem = {
      variation: selected.id,
      quantity: quantity,
    };

    // Check for duplicates
    if (isDuplicate) {
      toast.error("Item already in cart");
    } else {
      dispatch(postCart(cartItem))
        .unwrap()
        .then(() => {
          toast.success("Successfully added to cart");
        })
        .catch((error) => {
          navigate("/register");
        });
    }
  };

  const selectedVariationStock = useMemo(() => {
    if (!singleProduct?.variations) return 0;

    // Find the selected variation based on selected options
    const selected = singleProduct.variations.find((v) => {
      const fieldsToCompare = ["size", "color", "brand", "litre"].filter(
        (field) => selectedVariation?.[field] !== undefined
      );

      return fieldsToCompare.every(
        (field) => v[field] === selectedVariation[field]
      );
    });

    return selected?.stock || 0;
  }, [selectedVariation, singleProduct?.variations]);

  return (
    <div className="container px-4 py-8 mx-auto bg-white sm:px-6 lg:px-8">
      <div className="mb-6">
        <BreadCrumb product={singleProduct} />
      </div>

      <div className="flex flex-col gap-10 lg:flex-row">
        {/* Product Gallery Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col lg:w-1/2"
        >
          <div className="relative mb-4 overflow-hidden border">
            <motion.img
              key={selectedImage}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              src={selectedImage || singleProduct?.gallery?.[0]?.url}
              alt="Selected Product"
              className="w-full h-[500px] object-cover rounded-2xl"
            />
          </div>

          <div className="flex gap-3 p-2 overflow-y-hidden overflow-x-auto scrollbar-hide">
            {singleProduct?.gallery?.map((image, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative cursor-pointer overflow-hidden ${
                  selectedImage === image?.url
                    ? "ring-2 ring-black"
                    : "ring-1 ring-gray-200"
                }`}
                onClick={() => setSelectedImage(image?.url)}
              >
                <img
                  src={image?.url || "/placeholder.svg"}
                  alt={`Product ${index}`}
                  className="object-cover w-20 h-20"
                />
                {selectedImage === image?.url && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 bg-black bg-opacity-10"
                  />
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Product Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="lg:w-1/2"
        >
          <div className="flex flex-col gap-6">
            <div>
              {singleProduct?.category && (
                <span className="text-sm font-medium tracking-wider text-gray-500 uppercase">
                  {singleProduct?.category?.name}
                </span>
              )}
              <h1 className="mt-1 text-3xl font-bold md:text-4xl">
                {singleProduct?.name}
              </h1>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center text-sm text-gray-600">
                  <FiTag className="mr-1" />
                  <span>SKU: {singleProduct?.sku}</span>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200"></div>

            {/* Variation Section */}
            {singleProduct?.variations &&
              singleProduct.variations.length > 0 && (
                <div className="space-y-6">
                  {/* Size Variation */}
                  {singleProduct?.variations?.some((v) => v.size) && (
                    <div className="space-y-3">
                      <label className="block font-medium text-gray-800">
                        Size
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          ...new Set(
                            singleProduct?.variations?.map((v) => v.size)
                          ),
                        ].map((size, index) => {
                          const isSelected = selectedVariation?.size === size;
                          return (
                            <motion.button
                              key={index}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isSelected}
                              className={`px-4 py-2 rounded-lg border ${
                                isSelected
                                  ? "bg-black text-white border-black"
                                  : "border-gray-300 hover:border-black"
                              } transition-all duration-200`}
                              onClick={() => handleVariationClick("size", size)}
                            >
                              {size}
                              {isSelected && (
                                <IoIosCheckmarkCircle className="inline-block ml-1 text-white" />
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Color Variation */}
                  {singleProduct?.variations?.some((v) => v.color) && (
                    <div className="space-y-3">
                      <label className="block font-medium text-gray-800">
                        Color
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          ...new Set(
                            singleProduct?.variations?.map((v) => v.color)
                          ),
                        ].map((color, index) => {
                          const isSelected = selectedVariation?.color === color;
                          return (
                            <motion.button
                              key={index}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isSelected}
                              className={`px-4 py-2 rounded-lg border ${
                                isSelected
                                  ? "bg-black text-white border-black"
                                  : "border-gray-300 hover:border-black"
                              } transition-all duration-200`}
                              onClick={() =>
                                handleVariationClick("color", color)
                              }
                            >
                              {color}
                              {isSelected && (
                                <IoIosCheckmarkCircle className="inline-block ml-1 text-white" />
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Brand Variation */}
                  {singleProduct?.variations?.some((v) => v.brand) && (
                    <div className="space-y-3">
                      <label className="block font-medium text-gray-800">
                        Brand
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          ...new Set(
                            singleProduct?.variations?.map((v) => v.brand)
                          ),
                        ].map((brand, index) => {
                          const isSelected = selectedVariation?.brand === brand;
                          return (
                            <motion.button
                              key={index}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isSelected}
                              className={`px-4 py-2 rounded-lg border ${
                                isSelected
                                  ? "bg-black text-white border-black"
                                  : "border-gray-300 hover:border-black"
                              } transition-all duration-200`}
                              onClick={() =>
                                handleVariationClick("brand", brand)
                              }
                            >
                              {brand}
                              {isSelected && (
                                <IoIosCheckmarkCircle className="inline-block ml-1 text-white" />
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Litre Variation */}
                  {singleProduct?.variations?.some((v) => v.litre) && (
                    <div className="space-y-3">
                      <label className="block font-medium text-gray-800">
                        Litre
                      </label>
                      <div className="flex flex-wrap gap-3">
                        {[
                          ...new Set(
                            singleProduct?.variations?.map((v) => v.litre)
                          ),
                        ].map((litre, index) => {
                          const isSelected = selectedVariation?.litre === litre;
                          return (
                            <motion.button
                              key={index}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={isSelected}
                              className={`px-4 py-2 rounded-lg border ${
                                isSelected
                                  ? "bg-black text-white border-black"
                                  : "border-gray-300 hover:border-black"
                              } transition-all duration-200`}
                              onClick={() =>
                                handleVariationClick("litre", litre)
                              }
                            >
                              {litre}
                              {isSelected && (
                                <IoIosCheckmarkCircle className="inline-block ml-1 text-white" />
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Quantity and Add to Cart Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <label className="font-medium text-gray-800">Quantity</label>
                <div className="flex items-center">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-l-lg bg-gray-50"
                    onClick={decrementQuantity}
                    disabled={quantity === 1}
                  >
                    <IoMdRemove
                      className={
                        quantity === 1 ? "text-gray-400" : "text-gray-700"
                      }
                    />
                  </motion.button>
                  <div className="flex items-center justify-center w-12 h-10 bg-white border-t border-b border-gray-300">
                    <span className="font-medium">{quantity}</span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-r-lg bg-gray-50"
                    onClick={incrementQuantity}
                  >
                    <IoMdAdd className="text-gray-700" />
                  </motion.button>
                </div>
              </div>
              {/* Price Section */}
              <div className="flex items-center gap-4 mt-4">
                {/* Display Selling Price */}
                <span className="text-2xl font-bold text-gray-900">
                  Rs.{" "}
                  {singleProduct?.variations?.length > 0
                    ? selectedVariation?.selling_price ||
                      singleProduct?.selling_price
                    : singleProduct?.selling_price}
                </span>

                {/* Display Original Price (if different from selling price) */}
                {singleProduct?.variations?.length ? (
                  <>
                    {selectedVariation?.selling_price !==
                      selectedVariation?.price && (
                      <span className="text-lg text-gray-400 line-through">
                        Rs. {selectedVariation?.price}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {singleProduct?.selling_price !== singleProduct?.price && (
                      <span className="text-lg text-gray-400 line-through">
                        Rs. {singleProduct?.price}
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-4">
                {/* Add to Cart Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={
                    isDuplicate ||
                    (singleProduct?.variations &&
                    singleProduct.variations.length > 0
                      ? !selectedVariation || selectedVariationStock <= 0
                      : false)
                  }
                  className={`flex-1 py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 ${
                    isDuplicate ||
                    (singleProduct?.variations &&
                    singleProduct.variations.length > 0
                      ? !selectedVariation || selectedVariationStock <= 0
                      : false)
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "bg-black text-white hover:bg-gray-800"
                  } transition-colors duration-200`}
                  onClick={handleAddToCart}
                >
                  <FiShoppingBag className="text-lg" />
                  {isDuplicate
                    ? "Already in cart"
                    : singleProduct?.variations &&
                      singleProduct.variations.length > 0
                    ? selectedVariationStock <= 0
                      ? "Out of Stock"
                      : "Add to Cart"
                    : "Add to Cart"}
                </motion.button>

                {/* Add to Wishlist Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isLoading}
                  onClick={toggleHeart}
                  className="flex items-center justify-center w-12 h-12 border border-gray-300 rounded-lg"
                >
                  <AnimatePresence mode="wait">
                    {alreadyWished ? (
                      <motion.div
                        key="filled"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <IoIosHeart className="text-2xl text-red-600" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="outline"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <IoMdHeartEmpty className="text-2xl" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Product Description Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-16"
      >
        <div className="pb-4 border-b border-gray-200">
          <button
            onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
            className="flex items-center justify-between w-full"
          >
            <h2 className="text-xl font-semibold">Product Description</h2>
            <motion.div
              animate={{ rotate: isDescriptionExpanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <HiOutlineChevronRight className="text-xl" />
            </motion.div>
          </button>
        </div>

        <AnimatePresence>
          {isDescriptionExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div
                className="py-6 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: singleProduct?.description }}
              ></div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Recommendation Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="mt-16"
      >
        {/* <h2 className="mb-8 text-2xl font-semibold">You May Also Like</h2> */}
        <Recommendation
          sub_category_id={singleProduct?.category?.id}
          id={singleProduct?.id}
        />
      </motion.div>
    </div>
  );
};

export default ProductDetail;
